import { useCallback, useEffect, useRef, useState } from 'react'
import type { Api } from '../api'
import { actingAs } from '../api/act-as'
import { LOCAL, USD, inCurrency, type Currency, type Me, type ReaderSettings } from '../api/types'

/**
 * THE DISPLAY CURRENCY A READER LAST CHOSE, remembered (036, 4 Oct 2026).
 *
 * The sales manager's ask: "auto-save the user's currency". Every report opened
 * in US dollars -- the owner's default since 23 Sep -- so a reader who works in
 * local currency, or in riyals, set the toggle again on every visit to every
 * report. Now the choice is stored per person, and every report opens in it.
 *
 * ---------------------------------------------------------------------------
 * ONE CURRENCY PER PERSON, ACROSS EVERY REPORT
 *
 * Picking riyals on Sales and finding dollars on Receivables reads as "it did
 * not save". So all three reports read and write the same choice.
 *
 * ---------------------------------------------------------------------------
 * IT ARRIVES WITH /me, NOT AFTER IT
 *
 * Every report waits for /me before it draws, so the remembered currency rides
 * that answer (App calls `applySettingsFromMe`). A report therefore OPENS in the
 * right currency, and its figures are fetched once -- a currency loaded after
 * the first render would have fetched every figure twice, in dollars and then
 * again, and these are the heaviest queries the platform runs.
 *
 * ---------------------------------------------------------------------------
 * ONLY THE READER'S OWN CLICK IS REMEMBERED
 *
 * `choose` is the currency toggle; it saves. `set` is everything else that
 * moves the currency -- a saved view being applied, the page resetting -- and
 * it does not: a view's currency belongs to the view, and applying "My AED
 * view" once should not change what every report opens in from then on. A tab's
 * DEFAULT view therefore still opens in its own currency; the remembered one is
 * what everything without a view of its own opens in.
 *
 * Saving is decoration and is quiet on failure, like every reader preference:
 * a currency that failed to save must never put a panel over the figures.
 *
 * ---------------------------------------------------------------------------
 * LOGIN AS
 *
 * The person acted as sees THEIR remembered currency, and a choice made while
 * acting is stored as theirs, with the administrator recorded as the actor.
 *
 * A report learns of a switch from App's surface (`viewAs` is the answer's
 * own address), and App stores the answer here BEFORE it moves the surface --
 * so by the time a report sees the new identity, this module already holds
 * that person's choice. Two edges remain, both handled below:
 *
 *  - An answer about a DIFFERENT person always applies, even with a save in
 *    flight. That save belongs to the person being left; letting it hold the
 *    answer back would open the administrator's reports in the currency they
 *    picked while acting as somebody else.
 *  - A choice is stored against whoever the header names when it is SENT, and
 *    the header moves at the click, before /me is even asked. So each queued
 *    choice carries the identity it was made under and is dropped if that is
 *    no longer the session's, rather than landing on the wrong person.
 */

let remembered: Currency | null = null
/** Whose /me answer `remembered` came from: the person it describes. */
let person: string | null = null
/** The newest choice not yet sent, with the Login As identity it was made under. */
let queued: { value: string; as: string | null } | null = null
let sending = false

/** The stored spelling: 'local', 'usd', or the code. */
function toSetting(c: Currency): string {
  return c.mode === 'local' ? 'local' : c.mode === 'usd' ? 'usd' : c.code
}

function fromSetting(s: ReaderSettings['currency'] | undefined): Currency | null {
  if (!s) return null
  if (s.value === 'local') return LOCAL
  if (s.value === 'usd') return USD
  // A code the server sends with no rate is one it no longer offers.
  return s.ratePerUsd ? inCurrency(s.value, s.ratePerUsd) : null
}

/**
 * Fed by App from every /me answer -- the target's under Login As.
 *
 * The same person read again: a choice of theirs still being sent is newer
 * than this answer, which may have been read before it landed, so it wins until
 * it has been written. A different person: Login As started or ended, and
 * their choice applies whatever was pending.
 */
export function applySettingsFromMe(me: Pick<Me, 'user' | 'settings'>): void {
  const who = me.user.trim().toLowerCase()
  if (who === person) {
    if (sending || queued !== null) return
    remembered = fromSetting(me.settings?.currency)
    return
  }
  person = who
  remembered = fromSetting(me.settings?.currency)
}

/** What a report opens in: the remembered currency, else dollars, the owner's default. */
export function openingCurrency(): Currency {
  return remembered ?? USD
}

/** Sends queued choices one at a time, newest last, so the server ends where the reader did. */
async function flush(api: Api): Promise<void> {
  if (sending) return
  sending = true
  try {
    while (queued !== null) {
      const { value, as } = queued
      queued = null
      // Made as somebody the session no longer is: sent now, it would be theirs.
      if (as !== actingAs()) continue
      try {
        await api.saveReaderSetting('currency', value)
      } catch {
        // Quiet. The reader keeps the currency for this session either way.
      }
    }
  } finally {
    sending = false
  }
}

/** Remember a choice the reader made, for every report they open next. */
export function rememberCurrency(api: Api, c: Currency): void {
  remembered = c
  queued = { value: toSetting(c), as: actingAs() }
  void flush(api)
}

/**
 * One report's currency.
 *
 * Returns the currency, `choose` for the reader's own click (remembered), and
 * `set` for everything else (applied only).
 *
 * `identity` is whoever the session is -- the person acted as under Login As.
 * When it changes the report moves to THEIR remembered currency, because Login
 * As means seeing what they would see; App has already stored their /me answer
 * by the time the new identity reaches this hook. A default view of theirs
 * still applies after it, as on any opening.
 */
export function useReportCurrency(
  api: Api,
  identity: string | null,
): [Currency, (c: Currency) => void, (c: Currency) => void] {
  const [currency, setCurrency] = useState<Currency>(openingCurrency)

  const seen = useRef(identity)
  useEffect(() => {
    if (seen.current === identity) return
    seen.current = identity
    setCurrency(openingCurrency())
  }, [identity])

  const choose = useCallback(
    (c: Currency) => {
      setCurrency(c)
      rememberCurrency(api, c)
    },
    [api],
  )

  return [currency, choose, setCurrency]
}
